import { EmployerJPState } from '@restworld/utils-common';
import { TFunction } from 'i18next';

// The day is the one in Rome, whatever the browser's time zone: a position put offline at 23:30 on
// 18/09 is stored as 21:30 UTC, and an employer abroad must still read "dal 18/09".
const romeDateFormatter = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'Europe/Rome',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

// `last_status_change_date` and `status_list[].inserted_at` are naive UTC timestamps without an
// offset, which `new Date()` would read as the browser's local time.
const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/;

export function formatRomeDate(date: string) {
  return romeDateFormatter.format(new Date(HAS_OFFSET.test(date) ? date : `${date}Z`));
}

export function badgeVariant({ jpState }: { jpState: EmployerJPState }) {
  const variant = 'neutral';
  switch (jpState) {
    case 'draft':
      return 'warning';
    case 'open':
      return 'success';
    case 'offline':
      return 'neutral';
    case 'closed':
      return 'disabled';
    default:
      return variant;
  }
}

export function bagdeText({
  jpState,
  offlineSinceDate,
  t,
}: {
  jpState: EmployerJPState;
  t: TFunction;
  /**
   * When the position went offline: `went_offline_at`, falling back to the last status change for
   * the positions that never got the stamp. Closed positions are dated from that day too: one the
   * cron closes 14 days after the employer paused it went offline on the pause day, and "chiusa dal
   * <closing day>" showed a date two weeks after billing had stopped (Davide Lombardi, 13/10/2026).
   * The two states keep different words because they allow different things: a paused position can
   * go back online, a closed one can only be duplicated.
   */
  offlineSinceDate?: string;
}) {
  const date = offlineSinceDate ? formatRomeDate(offlineSinceDate) : null;
  switch (jpState) {
    case 'draft':
      return t('draft');
    case 'pending_approval':
      return t('pending_approval');
    case 'open':
      return t('open');
    case 'offline':
      return date ? t('paused_since', { date }) : t('paused');
    default:
      return date ? t('closed_offline_since', { date }) : t('closed');
  }
}
